// ═══════════════════════════════════════════════════════════════
// Offline Indicator — shows banner when offline, syncs queue on reconnect
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import { Text, Badge } from '../gab/GabComponents';
import { IconWifiOff, IconRefresh } from '@tabler/icons-react';

function requestQueueSize() {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'get-queue-size' });
  }
}

function flushQueue() {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return;
  // Try Background Sync first, fall back to message
  if ('SyncManager' in window) {
    navigator.serviceWorker.ready
      .then(reg => (reg as ServiceWorkerRegistration & { sync?: { register: (tag: string) => Promise<void> } }).sync?.register('flush-offline-queue'))
      .catch(() => navigator.serviceWorker.controller?.postMessage({ type: 'flush-queue' }));
  } else {
    navigator.serviceWorker.controller.postMessage({ type: 'flush-queue' });
  }
}

export default function OfflineIndicator() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [queueSize, setQueueSize] = useState(0);
  const [justReconnected, setJustReconnected] = useState(false);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Listen for queue-size messages from SW
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'queue-size') {
        setQueueSize(event.data.size ?? 0);
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    requestQueueSize();
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, []);

  useEffect(() => {
    const goOffline = () => {
      setIsOffline(true);
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
    const goOnline = () => {
      setIsOffline(false);
      setJustReconnected(true);
      flushQueue();
      reconnectTimer.current = setTimeout(() => {
        setJustReconnected(false);
        setQueueSize(0);
      }, 5000);
    };

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, []);

  if (!isOffline && !justReconnected) return null;

  if (justReconnected) {
    return (
      <div style={{
        position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
        zIndex: 9999, padding: '8px 20px', borderRadius: 8,
        background: '#16a34a', color: 'white',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        display: 'flex', alignItems: 'center', gap: 8,
        animation: 'fadeIn 0.3s ease',
      }}>
        <IconRefresh size={16} />
        <Text size="sm" fw={500}>
          Back online{queueSize > 0 ? ` — syncing ${queueSize} queued change${queueSize !== 1 ? 's' : ''}...` : ' — all changes synced'}
        </Text>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
      zIndex: 9999, padding: '8px 20px', borderRadius: 8,
      background: '#dc2626', color: 'white',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <IconWifiOff size={16} />
      <Text size="sm" fw={500}>You are offline</Text>
      {queueSize > 0 && (
        <Badge size="sm" color="yellow" variant="filled">
          {queueSize} queued
        </Badge>
      )}
    </div>
  );
}

export { OfflineIndicator };
